// lens client-side ed25519 signing — the operator's proof-of-possession for
// live/start + live/stop.
//
// These bytes MUST be byte-identical to what the server verifies in
// functions/_shared/auth.ts (canonicalLiveStart / canonicalLiveStop /
// canonicalShareMint / canonicalShareRevoke):
//   live-start:   JSON.stringify({ token, action, cohort_id, nonce, timestamp })
//   live-stop:    JSON.stringify({ token, action, nonce, timestamp })
//   share-mint:   JSON.stringify({ token, action, share_id, expires_at, nonce, timestamp })
//   share-revoke: JSON.stringify({ token, action, share_id, nonce, timestamp })
// with the fixed insertion order shown and no whitespace. live-start carries the
// device's self-asserted opaque Locus cohort id (or null) so the asserted id is
// signature-covered; the share writes carry the share id (+ expiry, mint only)
// so those are signature-covered too. If this drifts, every operator signature
// stops verifying — so the shape is kept deliberately in lock-step, the same way
// Locus's shared/sign.ts mirrors its server canon.
//
// We import b64url* from @cf/device-identity (the one shared contract surface);
// the lens-specific live-start / live-stop message SHAPES stay here, NOT in the
// package — adding app message types to the shared contract would leak app
// concepts into it (per the spec + the auth.ts header note).
//
// Only the browser signs; crypto.subtle is available in the SPA runtime.

import { b64urlEncode, b64urlDecode } from '@cf/device-identity';

const enc = new TextEncoder();

export interface StoredKeypair {
  priv: string; // base64url pkcs8
  pub: string; // base64url raw (32 bytes)
}

export type LiveAction = 'live-start' | 'live-stop';

// The canonical bytes for a signed live write. Mirrors the server's
// canonicalLiveStart / canonicalLiveStop exactly (same key order, no whitespace).
// live-start carries the self-asserted opaque cohort id (or null) between
// `action` and `nonce`; live-stop has no cohort_id (cohortId is ignored).
export function canonicalLive(
  token: string,
  action: LiveAction,
  nonce: string,
  timestamp: number,
  cohortId: string | null = null,
): Uint8Array {
  const canonical = action === 'live-start'
    ? { token, action, cohort_id: cohortId, nonce, timestamp }
    : { token, action, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

export async function generateKeypair(): Promise<StoredKeypair> {
  const kp = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
    'sign',
    'verify',
  ])) as CryptoKeyPair;
  const privBuf = await crypto.subtle.exportKey('pkcs8', kp.privateKey);
  const pubBuf = await crypto.subtle.exportKey('raw', kp.publicKey);
  return {
    priv: b64urlEncode(new Uint8Array(privBuf)),
    pub: b64urlEncode(new Uint8Array(pubBuf)),
  };
}

export async function importPrivateKey(b64urlPkcs8: string): Promise<CryptoKey> {
  const raw = b64urlDecode(b64urlPkcs8);
  return crypto.subtle.importKey('pkcs8', raw as BufferSource, { name: 'Ed25519' }, false, ['sign']);
}

export function randomNonceB64(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return b64urlEncode(bytes);
}

// Build the X-Lens-* signed-write headers for a live/start or live/stop call.
// Returns the headers the server reads in functions/_shared/auth.ts plus the
// nonce/timestamp echoed for the body (the server reads the token + cohort_id
// from the body and everything else from the headers + canonical bytes).
//
// For live-start, pass the device's self-asserted opaque cohort id (or null) —
// it is folded into the signed bytes AND must be echoed in the request body
// (start.ts re-derives the canon from body.cohort_id and verifies the
// signature, so the two must agree). live-stop ignores cohortId.
export async function signLiveWrite(
  privB64Pkcs8: string,
  token: string,
  action: LiveAction,
  cohortId: string | null = null,
): Promise<{ headers: Record<string, string>; nonce: string; timestamp: number; cohortId: string | null }> {
  const privKey = await importPrivateKey(privB64Pkcs8);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const sig = await crypto.subtle.sign(
    { name: 'Ed25519' },
    privKey,
    canonicalLive(token, action, nonce, timestamp, cohortId) as BufferSource,
  );
  return {
    headers: {
      'content-type': 'application/json',
      'X-Lens-Signature': b64urlEncode(new Uint8Array(sig)),
      'X-Lens-Nonce': nonce,
      'X-Lens-Timestamp': String(timestamp),
    },
    nonce,
    timestamp,
    cohortId,
  };
}

// A fresh opaque, high-entropy share id, generated DEVICE-SIDE so it can be
// folded into the signed mint canon (the server verifies + stores exactly the
// id the device signed; it never substitutes one). 24 random bytes = 192 bits,
// well over the ≥128-bit floor, base64url. Explicitly NOT derived from the
// device token — that independence is what makes a share revocable without
// rotating the device key (the v1 unlisted handle WAS the token).
export function randomShareId(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return b64urlEncode(bytes);
}

export type ShareAction = 'share-mint' | 'share-revoke';

// Canonical bytes for a signed share write. Mirrors the server's
// canonicalShareMint / canonicalShareRevoke exactly (same key order, no
// whitespace). share-mint carries the (server-capped) expires_at between
// `share_id` and `nonce`; share-revoke has no expires_at.
export function canonicalShare(
  token: string,
  action: ShareAction,
  shareId: string,
  nonce: string,
  timestamp: number,
  expiresAt: number,
): Uint8Array {
  const canonical = action === 'share-mint'
    ? { token, action, share_id: shareId, expires_at: expiresAt, nonce, timestamp }
    : { token, action, share_id: shareId, nonce, timestamp };
  return enc.encode(JSON.stringify(canonical));
}

// Build the X-Lens-* signed-write headers for a share mint or revoke call.
//
// For share-mint the device CHOOSES `share_id` + `expires_at` and signs both, so
// the server's stored row is what the device authenticated (the server only caps
// `expires_at` downward — if it caps, the device must re-sign with the capped
// value, so the SPA should pre-cap to ≤ now+7d). For share-revoke pass the
// share_id to revoke; `expiresAt` is ignored. The signed values are echoed in
// the request body so the server re-derives the canon and verifies it agrees.
export async function signShareWrite(
  privB64Pkcs8: string,
  token: string,
  action: ShareAction,
  shareId: string,
  expiresAt = 0,
): Promise<{ headers: Record<string, string>; nonce: string; timestamp: number }> {
  const privKey = await importPrivateKey(privB64Pkcs8);
  const timestamp = Date.now();
  const nonce = randomNonceB64();
  const sig = await crypto.subtle.sign(
    { name: 'Ed25519' },
    privKey,
    canonicalShare(token, action, shareId, nonce, timestamp, expiresAt) as BufferSource,
  );
  return {
    headers: {
      'content-type': 'application/json',
      'X-Lens-Signature': b64urlEncode(new Uint8Array(sig)),
      'X-Lens-Nonce': nonce,
      'X-Lens-Timestamp': String(timestamp),
    },
    nonce,
    timestamp,
  };
}
